import React, { useRef, useState } from "react";
import { Col, Divider, Form, Modal, Row, Space, Tooltip } from 'antd';
import { useDispatch } from "react-redux";

import { appManagementActions } from "@/redux/actions";
import { useNotification } from "@/hooks";
import { cleanObject } from '@/utils';
import { TextField } from '@/components/Form';

import { CopyOutlined, EyeInvisibleOutlined, EyeOutlined, RedoOutlined } from "@ant-design/icons"

const {confirm} = Modal;

const AppForm = ({
                     form,
                     formId,
                     detailData,
                     isCreating,
                     onSubmit
                 }) => {
    const dispatch = useDispatch();
    const {showErrorMessage, showSuccessMessage} = useNotification();
    const clientAppRef = useRef(null);
    const secretRef = useRef(null);

    const [arrHidden, setArrHidden] = useState([]);

    const handleSubmit = (values) => {
        onSubmit(cleanObject(values));
    }

    const refreshSecretKey = () => {
        dispatch(appManagementActions.renewSecretKey({
            params: {id: detailData.id},
            onCompleted: (res) => {
                form.setFieldsValue(res.data);
                showSuccessMessage("Revoke secret key successfully");
            },
            onError: (err) => {
                console.log({err})
                showErrorMessage("Revoke secret key failed");
            }
        }))
    }

    const refreshClientAppId = () => {
        dispatch(appManagementActions.renewClientAppId({
            params: {id: detailData.id},
            onCompleted: (res) => {
                form.setFieldsValue(res.data);
                showSuccessMessage("Revoke client app id successfully");
            },
            onError: (err) => {
                showErrorMessage("Revoke client app id failed");
            }
        }))
    }

    const onRefresh = (type) => {
        confirm({
            title: `Are you sure to revoke ${type === 'secretKey' ? 'Secret' : "Client app ID"}?`,
            okText: 'Yes',
            cancelText: 'No',
            okType: 'danger',
            centered: true,
            onOk: () => {
                return type === 'secretKey' ? refreshSecretKey() : refreshClientAppId()
            }
        });
    }

    const addOnAfter = (index, ref, type) => {
        return (
            <Space>
                {arrHidden.includes(index) ?
                    <EyeOutlined style={{cursor: "pointer"}} onClick={() => togglePassword(index)}/>
                    :
                    <EyeInvisibleOutlined style={{cursor: "pointer"}} onClick={() => togglePassword(index)}/>
                }
                <Divider type="vertical"/>
                <Tooltip trigger="click" title="copied">
                    <CopyOutlined style={{cursor: "pointer"}} onClick={() => copyText(ref)}/>
                </Tooltip>
                <Divider type="vertical"/>
                <RedoOutlined style={{cursor: "pointer"}}
                              onClick={() => onRefresh(type)}/>
            </Space>
        )
    }

    const togglePassword = (index) => {
        if (arrHidden.includes(index)) {
            setArrHidden(arrHidden.filter(item => item !== index));
        } else {
            setArrHidden([...arrHidden, index]);
        }
    }

    const copyText = (ref) => {
        const {input, setSelectionRange} = ref.current;
        if (ref.current) {
            setSelectionRange(0, 99999);
            navigator.clipboard.writeText(input.value);
        }
    }

    return (
        <Form
            id={formId}
            onFinish={handleSubmit}
            form={form}
            layout="vertical"
            initialValues={detailData}
        >
            <Row gutter={16}>
                <Col span={24}>
                    <TextField
                        fieldName="name"
                        label="Service"
                        required
                    />
                </Col>
                <Col span={24}>
                    <TextField
                        fieldName="id"
                        label="ID"
                        hidden={isCreating}
                        rules={[{type: 'number'}, {required: !isCreating}]}
                        disabled
                    />
                </Col>
                <Col span={24}>
                    <TextField
                        type={arrHidden.includes(1) ? "password" : "text"}
                        fieldName="clientAppId"
                        label="Client app ID"
                        hidden={isCreating}
                        insideRef={clientAppRef}
                        addonAfter={addOnAfter(1, clientAppRef, "clientAppId")}
                        required={!isCreating}
                        disabled
                    />
                </Col>
                <Col span={24}>
                    <TextField
                        type={arrHidden.includes(2) ? "password" : "text"}
                        fieldName="secretKey"
                        label="Secret key"
                        hidden={isCreating}
                        insideRef={secretRef}
                        addonAfter={addOnAfter(2, secretRef, "secretKey")}
                        required={!isCreating}
                        disabled
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default React.memo(AppForm);